"use client"

import { useState, useEffect } from "react"
import { useAuth, signOut } from "@/hooks/useAuth"
import { usePathname } from "next/navigation"
import { useDarkMode } from "@/contexts/DarkModeContext"
import { Topbar } from "./nav/Topbar"
import { DesktopSidebar } from "./nav/DesktopSidebar"
import { MobileSidebar } from "./nav/MobileSidebar"
import { SECTIONS, NAV_GROUPS, isGroupChildActive } from "./nav/nav-config"

export default function ModernNavbar() {
  const { data: session, status } = useAuth()
  const pathname = usePathname()
  useDarkMode()

  // Stany Menu
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // Stan Sidebar (Zwinięty/Rozwinięty)
  const [isSidebarCollapsed, setSidebarCollapsed] = useState(false)

  // Stan Akordeonów (które sekcje są otwarte)
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    "Główne": true,
    "Inne": true,
    "Zarządzanie": true,
    "System": true
  })

  // Stan rozwijanych grup (np. Podręcznik, Katalog WWW)
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    "katalog-maszyn": true,
    "podrecznik": false
  })

  const isLoading = status === "loading"
  const isAdmin = session?.user?.role === "ADMIN"
  const isDeveloper = !!session?.user?.isDeveloper

  // Automatyczne otwieranie grupy, jeśli użytkownik nawiguje do jej podstrony
  useEffect(() => {
    for (const group of NAV_GROUPS) {
      if (group.children.some((c) => isGroupChildActive(c.href, pathname))) {
        setOpenGroups((prev) => (prev[group.id] ? prev : { ...prev, [group.id]: true }))
      }
    }
  }, [pathname])

  // --- LOGIKA ---

  // Przywracanie stanu sidebara z localStorage
  useEffect(() => {
    const stored = localStorage.getItem("sidebar-collapsed")
    if (stored) setSidebarCollapsed(stored === "true")
  }, [])

  // Zapisywanie stanu sidebara
  const setCollapsed = (next: boolean) => {
    setSidebarCollapsed(next)
    localStorage.setItem("sidebar-collapsed", String(next))
  }

  // --- RENDER ---
  return (
    <>
      <Topbar
        auth={{ data: session, status }}
        onOpenMobileMenu={() => setIsMobileMenuOpen(true)}
        onSignOut={() => {
          void signOut()
        }}
      />

      <DesktopSidebar
        pathname={pathname}
        isAdmin={isAdmin}
        isDeveloper={isDeveloper}
        collapsed={isSidebarCollapsed}
        setCollapsed={setCollapsed}
        openSections={openSections}
        setOpenSections={setOpenSections}
        openGroups={openGroups}
        setOpenGroups={setOpenGroups}
      />

      <MobileSidebar
        open={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        pathname={pathname}
        isAdmin={isAdmin}
        isDeveloper={isDeveloper}
        openSections={openSections}
        setOpenSections={setOpenSections}
        openGroups={openGroups}
        setOpenGroups={setOpenGroups}
      />

      {/* --- SPACERS --- */}
      {/* Spacer pod topbar */}
      <div className="h-16" />
      {/* Spacer dla treści (zależny od szerokości sidebara) */}
      <div
        className={`hidden lg:block transition-[margin] duration-300 ease-in-out ${isSidebarCollapsed ? 'lg:ml-[72px]' : 'lg:ml-64'}`}
      />
    </>
  )
}